import React from 'react';
import {
  Layout,
  Stack,
  Card,
  Select,
  Text,
  SwitcherControl,
  Tooltip,
  Icon,
  Switcher,
  Trans,
  Link,
} from '@piwikpro/ui-components';
import { useTranslation } from 'react-i18next';
import { FormikProps } from 'formik';
import { withInject } from '@piwikpro/platform';
import CookieInformationAccountComponent
  from '@piwikpro/cookie-information-crate/components/CookieInformationAccount/CookieInformationAccount';
import { ConsentManagementToolInfo } from '../../ConsentManagementToolInfo/ConsentManagementToolInfo';
import { ConsentManagementTool } from '../../../model';
import type { FormValues } from '../types';

type ConsentSelection = ConsentManagementTool | null;
type ConsentSelectionOption = ConsentManagementTool | 'none';

interface ConsentOptionItem {
  id: ConsentSelectionOption
  value: ConsentSelectionOption
  name: string
}

interface ConsentOptions {
  id: number
  name: string
  items: ConsentOptionItem[]
}

interface InjectedProps {
  CookieInformationAccount: typeof CookieInformationAccountComponent
}

interface ConsentManagerStepProps extends InjectedProps {
  consentOption: ConsentSelection
  onChangeConsentOption: (value: ConsentSelection) => void
  isPiwikProConsentManagerIncludedInLicense: boolean
  loggedUserEmail?: string | null
  setCookieFormDirty: (value: boolean) => void
  api: FormikProps<FormValues>
}

const convertUrlsToCanonicalDomains = (urls: string[] = []): string[] => urls.map((url) => {
  try {
    if (!url.includes('://')) {
      throw new Error('Protocol expected');
    }
    const urlObj = new URL(url);
    return urlObj.hostname;
  } catch {
    return url;
  }
});

const createConsentOption = (id: ConsentSelectionOption, name: string): ConsentOptionItem => ({
  id,
  value: id,
  name,
});

const mapConsentSelectionOptionToValue = (value: ConsentSelectionOption): ConsentSelection => (
  value === 'none' ? null : value
);

function ConsentManagerStep({
  CookieInformationAccount,
  consentOption,
  onChangeConsentOption,
  isPiwikProConsentManagerIncludedInLicense,
  loggedUserEmail,
  setCookieFormDirty,
  api,
}: ConsentManagerStepProps) {
  const { t } = useTranslation(['administration', 'consent-settings']);
  const { values } = api;
  const cookieInformationDomains = convertUrlsToCanonicalDomains(values.urls);
  const consentOptions: ConsentOptions[] = [
    {
      id: 0,
      name: '',
      items: [
        createConsentOption('CookieInformationCMP', t('consent-settings:consent-management-tool.cookie-information-long')),
        ...(isPiwikProConsentManagerIncludedInLicense ? [
          createConsentOption('PiwikLegacy', t('consent-settings:consent-management-tool.legacy')),
        ] : []),
        createConsentOption('OtherConsentTool', t('consent-settings:consent-management-tool.other-long')),
        createConsentOption('none', t('consent-settings:consent-management-tool.none-long')),
      ],
    },
  ];

  return (
    <Layout maxWidth="medium">
      <Stack.Item fill>
        <Card connected={consentOption !== null}>
          <Card.Section>
            <Stack vertical>
              <Stack.Item>
                <Text as="p" weight={600} dataId="Label">
                  {t('apps.creator.consentManagementLabel')}
                </Text>
              </Stack.Item>
              <Stack.Item previousSpacing="narrow">
                {!isPiwikProConsentManagerIncludedInLicense
                  ? (
                    <Text as="p" size={12} color="InkLighter" dataId="Caption">
                      {t('consent-settings:consent-management-tool.init-description')}
                    </Text>
                  )
                  : (
                    <Text as="p" size={12} color="InkLighter" dataId="Caption">
                      {t('consent-settings:consent-management-tool.init-description-with-legacy-cm')}
                    </Text>
                  )
                }
              </Stack.Item>
              <Stack.Item fill>
                <Select
                  name="consentView"
                  value={consentOption ?? 'none'}
                  onChange={event => onChangeConsentOption(
                    mapConsentSelectionOptionToValue(event.target.value as ConsentSelectionOption),
                  )}
                  options={consentOptions}
                />
              </Stack.Item>
              {(consentOption === 'PiwikLegacy' || consentOption === 'OtherConsentTool') && (
                <Stack.Item fill>
                  <ConsentManagementToolInfo tool={consentOption} t={t} />
                </Stack.Item>
              )}
            </Stack>
          </Card.Section>
          {consentOption === 'CookieInformationCMP' && (
            <Card.Section subdued>
              <CookieInformationAccount onDirtyChange={setCookieFormDirty} loggedUserEmail={loggedUserEmail} domains={cookieInformationDomains} />
            </Card.Section>
          )}
        </Card>
        {consentOption !== null && (
          <>
            <Card connected>
              <Card.Section>
                <Stack vertical spacing="wide">
                  <Stack.Item>
                    <Text as="p" weight={600}>{t('apps.creator.sessionAndVisitorIdentifiers.title')}</Text>
                  </Stack.Item>
                  <Stack.Item previousSpacing="narrow">
                    <Text as="p" size={12} color="InkLighter" dataId="Caption">
                      <Trans
                        t={t}
                        i18nKey="apps.creator.sessionAndVisitorIdentifiers.description"
                        parent="span"
                        components={[
                          <Link size="small" target="_blank" href="https://help.piwik.pro/support/questions/what-are-sessions-and-how-are-they-counted/#session-and-visitor-identifiers">
                            read more
                          </Link>,
                        ]}
                      />
                    </Text>
                  </Stack.Item>
                </Stack>
              </Card.Section>
              <Card.Section subdued>
                <Stack vertical>
                  <Stack.Item fill>
                    <Stack verticalAlignment="center">
                      <Stack.Item>
                        <SwitcherControl
                          name="use_session_hash"
                        />
                      </Stack.Item>
                      <Stack.Item>
                        <Text as="p" weight={600} dataId="Label">
                          {t('apps.creator.sessionAndVisitorIdentifiers.useSessionHashSwitcherLabel')}
                        </Text>
                      </Stack.Item>
                      <Stack.Item previousSpacing="narrow">
                        <Tooltip content={t('apps.creator.sessionAndVisitorIdentifiers.tooltip')}>
                          <Icon name="info-circle-outline" />
                        </Tooltip>
                      </Stack.Item>
                    </Stack>
                  </Stack.Item>
                  <Stack.Item>
                    <Stack verticalAlignment="center">
                      <Stack.Item>
                        <SwitcherControl
                          name="privacyUseCookies"
                        />
                      </Stack.Item>
                      <Stack.Item>
                        <Text as="p" weight={600} dataId="Label">
                          {t('apps.creator.sessionAndVisitorIdentifiers.useVisitorCookieSwitcherLabel')}
                        </Text>
                      </Stack.Item>
                      <Stack.Item previousSpacing="narrow">
                        <Tooltip content={t('apps.creator.sessionAndVisitorIdentifiers.useVisitorCookieTooltip')}>
                          <Icon name="info-circle-outline" />
                        </Tooltip>
                      </Stack.Item>
                    </Stack>
                  </Stack.Item>
                </Stack>
              </Card.Section>
            </Card>
            <Card>
              <Card.Section>
                <Stack verticalAlignment="center">
                  <Stack.Item fill>
                    <Stack vertical spacing="wide">
                      <Stack.Item>
                        <Text as="p" weight={600}>{t('apps.creator.collectAnonymousData.title')}</Text>
                      </Stack.Item>
                      <Stack.Item previousSpacing="narrow">
                        <Text as="p" size={12} color="InkLighter" dataId="Caption">
                          {t('apps.creator.collectAnonymousData.description')}
                        </Text>
                      </Stack.Item>
                    </Stack>
                  </Stack.Item>
                  <Stack.Item>
                    <SwitcherControl
                      name="gdprDataAnonymization"
                    />
                  </Stack.Item>
                </Stack>
              </Card.Section>
              {values.gdprDataAnonymization && (
                <Card.Section subdued>
                  <Stack vertical>
                    <Stack.Item fill>
                      <Stack verticalAlignment="center">
                        <Stack.Item>
                          <SwitcherControl
                            name="use_anonymous_session_hash"
                          />
                        </Stack.Item>
                        <Stack.Item>
                          <Text as="p" weight={600} dataId="Label">
                            {t('apps.creator.collectAnonymousData.useSessionHashSwitcherLabel')}
                          </Text>
                        </Stack.Item>
                        <Stack.Item previousSpacing="narrow">
                          <Tooltip content={t('apps.creator.collectAnonymousData.useSessionHashTooltip')}>
                            <div data-ppuc-id="collectAnonymousDataUseSessionHashTooltip">
                              <Icon name="info-circle-outline" />
                            </div>
                          </Tooltip>
                        </Stack.Item>
                      </Stack>
                    </Stack.Item>
                    <Stack.Item>
                      <Stack verticalAlignment="center">
                        <Stack.Item>
                          <Switcher
                            name="gdprDataAnonymizationMode"
                            value={values.gdprDataAnonymizationMode === 'session_cookie_id'}
                            onChange={(event) => {
                              const isChecked = event.target.value;
                              api?.setFieldValue(
                                'gdprDataAnonymizationMode',
                                isChecked ? 'session_cookie_id' : 'no_device_storage',
                              );
                            }}
                          />
                        </Stack.Item>
                        <Stack.Item>
                          <Text as="p" weight={600} dataId="Label">
                            {t('apps.creator.collectAnonymousData.useVisitorCookieSwitcherLabel')}
                          </Text>
                        </Stack.Item>
                        <Stack.Item previousSpacing="narrow">
                          <Tooltip content={t('apps.creator.collectAnonymousData.useVisitorCookieTooltip')}>
                            <div data-ppuc-id="collectAnonymousDataUseVisitorCookieTooltip">
                              <Icon name="info-circle-outline" />
                            </div>
                          </Tooltip>
                        </Stack.Item>
                      </Stack>
                    </Stack.Item>
                  </Stack>
                </Card.Section>
              )}
            </Card>
          </>
        )}
      </Stack.Item>
    </Layout>
  );
}

export default withInject<InjectedProps>({
  CookieInformationAccount: 'CookieInformationCrate.components.CookieInformationAccount',
})(ConsentManagerStep);
